import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { 
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  Circle,
  ArrowLeft,
  BookOpen,
  Award,
  Clock
} from "lucide-react";

export default function SOPViewerPage() {
  const { sopId } = useParams();
  const navigate = useNavigate();
  const [sop, setSop] = useState(null);
  const [progress, setProgress] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [sopId]);

  const fetchData = async () => {
    try {
      const [sopRes, progressRes] = await Promise.all([
        apiClient.get(`/sops/${sopId}`),
        apiClient.get(`/progress/${sopId}`),
      ]);
      setSop(sopRes.data);
      setProgress(progressRes.data);

      const completedSteps = progressRes.data.completed_steps || [];
      const firstIncomplete = sopRes.data.steps.findIndex(
        (_, index) => !completedSteps.includes(index)
      );
      setCurrentStep(firstIncomplete >= 0 ? firstIncomplete : 0);
    } catch (error) {
      console.error("Error fetching SOP:", error);
      toast.error("Failed to load training module");
      navigate("/sops");
    } finally {
      setLoading(false);
    }
  };

  const markStepComplete = async (stepIndex) => {
    const completedSteps = [...(progress?.completed_steps || [])];
    if (!completedSteps.includes(stepIndex)) {
      completedSteps.push(stepIndex);
    }

    try {
      const response = await apiClient.put(`/progress/${sopId}`, {
        sop_id: sopId,
        completed_steps: completedSteps,
        quiz_score: progress?.quiz_score,
        quiz_passed: progress?.quiz_passed,
        completed: progress?.completed || false,
      });
      setProgress(response.data);
    } catch (error) {
      console.error("Error updating progress:", error);
    }
  };

  const handleNext = () => {
    markStepComplete(currentStep);
    if (currentStep < sop.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (index) => {
    setCurrentStep(index);
  };

  const handleStartQuiz = () => {
    markStepComplete(currentStep);
    navigate(`/sops/${sopId}/quiz`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6 max-w-4xl mx-auto">
          <div className="skeleton h-8 w-32"></div>
          <div className="skeleton h-64 w-full"></div>
          <div className="skeleton h-40 w-full"></div>
        </div>
      </Layout>
    );
  }

  if (!sop) return null;

  const step = sop.steps[currentStep];
  const isStepCompleted = progress?.completed_steps?.includes(currentStep);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fade-in-up">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/sops")}
          className="text-[hsl(var(--muted-foreground))] hover:text-[var(--rd-green)] -ml-2"
          data-testid="back-to-sops-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Training
        </Button>

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-[var(--rd-gold)] mb-2">
              {sop.category.replace("_", " ")}
            </p>
            <h1 className="text-2xl lg:text-3xl text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
              {sop.title}
            </h1>
            <p className="text-[hsl(var(--muted-foreground))] mt-2">
              {sop.description}
            </p>
          </div>
          {progress?.completed && (
            <div className="badge-gold flex items-center gap-1.5 whitespace-nowrap">
              <Award className="w-4 h-4" />
              Certified
            </div>
          )}
        </div>

        {/* Step Progress Indicator */}
        <div className="flex items-center gap-2 py-4 overflow-x-auto">
          {sop.steps.map((s, index) => {
            const isComplete = progress?.completed_steps?.includes(index);
            const isCurrent = index === currentStep;

            return (
              <button
                key={index}
                onClick={() => handleStepClick(index)}
                className={`flex items-center gap-2 px-4 py-2 rounded-sm transition-colors whitespace-nowrap text-sm ${
                  isCurrent
                    ? "bg-[var(--rd-green)] text-white"
                    : isComplete
                    ? "bg-[var(--rd-gold)]/10 text-[var(--rd-gold)]"
                    : "bg-white text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]"
                }`}
                data-testid={`step-indicator-${index}`}
              >
                {isComplete ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <Circle className="w-4 h-4" />
                )}
                <span className="font-medium">Step {index + 1}</span>
              </button>
            );
          })}
        </div>

        {/* Current Step Content */}
        <Card className="border-[hsl(var(--border))] overflow-hidden">
          {step.image_url && (
            <div className="h-56 lg:h-72 overflow-hidden">
              <img
                src={step.image_url}
                alt={step.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <CardContent className="p-6 lg:p-8">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs font-semibold tracking-wider uppercase text-[var(--rd-gold)]">
                    Step {step.order} of {sop.steps.length}
                  </span>
                  {step.timing && (
                    <span className="timing-badge">
                      <Clock className="w-3 h-3" />
                      {step.timing}
                    </span>
                  )}
                </div>
                <h2 className="text-xl lg:text-2xl text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
                  {step.title}
                </h2>
              </div>
              {isStepCompleted && (
                <div className="flex items-center gap-1.5 text-[var(--rd-gold)]">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="text-sm font-medium">Done</span>
                </div>
              )}
            </div>

            <p className="text-base lg:text-lg text-[hsl(var(--foreground))] leading-relaxed mb-8">
              {step.description}
            </p>

            {step.key_points && step.key_points.length > 0 && (
              <div className="bg-[hsl(var(--surface))] rounded-sm p-5 border border-[hsl(var(--border))]">
                <h3 className="text-xs font-semibold tracking-wider uppercase text-[hsl(var(--muted-foreground))] mb-4">
                  Key Points
                </h3>
                <ul className="space-y-3">
                  {step.key_points.map((point, index) => (
                    <li key={index} className="key-point">
                      <div className="key-point-marker"></div>
                      <span className="text-[hsl(var(--foreground))]">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="border-[var(--rd-green)] text-[var(--rd-green)] hover:bg-[var(--rd-green)] hover:text-white disabled:opacity-50"
            data-testid="prev-step-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          {currentStep === sop.steps.length - 1 ? (
            <div className="flex gap-3">
              {!isStepCompleted && (
                <Button
                  onClick={() => markStepComplete(currentStep)}
                  className="bg-[var(--rd-gold)] hover:bg-[var(--rd-gold)]/90 text-white"
                  data-testid="complete-step-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Mark Complete
                </Button>
              )}
              {sop.quiz_questions?.length > 0 && (
                <Button
                  onClick={handleStartQuiz}
                  className="bg-[var(--rd-green)] hover:bg-[var(--rd-green-dark)] text-white"
                  data-testid="start-quiz-btn"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Take Assessment
                </Button>
              )}
            </div>
          ) : (
            <Button
              onClick={handleNext}
              className="bg-[var(--rd-green)] hover:bg-[var(--rd-green-dark)] text-white"
              data-testid="next-step-btn"
            >
              Next Step
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
}
